
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {mapActions, mapGetters} from "vuex";
import {Scene} from "../interfaces/Interfaces";
import ScenesSelectionList from "./ScenesSelectionList.vue"

@Component ({
  components: {
    ScenesSelectionList
  },
  methods: {
    ...mapActions('scenes', {
      addScene: 'addScene'
    }),
    ...mapActions('scenes', {
      updateScene: 'updateScene'
    }),
    ...mapActions('scenes', {
      deleteScene: 'deleteScene'
    })
  },
  computed: {
    ...mapGetters('scenes', {
      getScenes: 'getScenes'
    }),
    ...mapGetters('stimulus', {
      getVideoId: 'getVideoId'
    })
  }
})
export default class ScenesSelection extends Vue {

  // Local variables
  private getScenes!: Scene[] | [] 
  private getVideoId!: string 
  private sceneBandEl: any 
  private bandWidth: number = 0
  private isVideoPlaying: boolean = false
  private videoTimestamp: number = 0
  private selectedSceneId: number|null = null  
  private didSceneMove: boolean = false
  private videoEl: any
  private lockedScenes: number[] | [] | any = []
  private allowCreateScenePropagation: boolean = true
  private isSelectFrame: boolean = false

  // Initiates methods imported from store
  private updateScene!: (updatedScene: Scene) => Scene
  private addScene!: (newScene: Scene) => Scene 
  private deleteScene!: (sceneId: number) => Scene 

  @Prop() private videoLengthInDeciseconds!: number
  @Prop() private videoUrl!: string

  // Watchers
  @Watch('bandWidth')
  private sceneOffset(sceneValue: number): number {
    const offset = (this.pixelsFromDeciseconds(sceneValue, this.bandWidth, this.videoLengthInDeciseconds) / this.bandWidth) *100 
    return offset
  }

  @Watch('bandWidth')
  @Watch('videoTimestamp')
  private liveLineOffset(): number {
    const px = this.pixelsFromSeconds(this.videoTimestamp, this.bandWidth, this.videoLengthInDeciseconds)
    const progress = (px/this.bandWidth) * 100
    return progress
  }

  @Watch("selectedSceneId")
  private setVideoCurrentTime(): void {
    if (this.videoEl && this.selectedSceneId) {
      const scene: Scene|undefined = this.getScenes.find((s:Scene)=> s.id === this.selectedSceneId)
      if (scene) {
        if (this.isSelectFrame && scene.frame_number) this.videoEl.currentTime = scene.frame_number /10
        else this.videoEl.currentTime = scene.start /10
      }
    }
  }

  // Handles for buttons group events
  private handlePlayScene(): void {
    if(this.videoEl) this.videoEl.play()
  }

  private handlePauseScene(): void {
    if(this.videoEl) this.videoEl.pause()
  }

  private handleLockSceneClick(): void {
    let {selectedSceneId: scene, lockedScenes: locked} = this
    if (scene !== null) {
      const position = locked.indexOf(scene)
      if(position !== -1) locked.splice(position, 1)
      else locked.push(scene)
    }
  }

  private handleDeleteScene(): void {
    if(this.selectedSceneId && !this.lockedScenes.includes(this.selectedSceneId)) {
      this.deleteScene(this.selectedSceneId)
      this.setInitialSelectedScene()
    }
  }

  private createScene(value: number): void {
    if (this.allowCreateScenePropagation) {
      const newScene: Scene = { video: this.getVideoId ,start: value, label: '', frame_number: value, frame: ''}
      this.addScene(newScene)
    }
  }

  private handleSetSceneFromButtonGroup(): void {
    this.enableCreateScene()
    const value: number = Math.round(this.videoTimestamp *10)
    this.createScene(value)
  }

  private enableCreateScene(): void {
    this.allowCreateScenePropagation = true
  }

  private handleSetScenesBandClickEvent(e: MouseEvent): void {
    if (this.allowCreateScenePropagation) {
      const coordInDeciseconds: number = Math.floor(this.decisecondsFromPixels(e.offsetX, this.bandWidth, this.videoLengthInDeciseconds))
      this.createScene(coordInDeciseconds)
    } else {
      this.enableCreateScene()
    }
  }

  // Functions to transform scenes values for different use
  private decisecondsFromPixels(coordInPx: number, scenesBandLength: number, videoLength: number): number {
    const coordInDeciseconds = (videoLength * coordInPx)/ scenesBandLength
    return coordInDeciseconds
  }

  private pixelsFromDeciseconds(coordInSec: number, scenesBandLength: number, videoLength: number): number {
    const coordInPixels = (scenesBandLength * coordInSec) / videoLength
    return coordInPixels
  }

  private pixelsFromSeconds(coordInSec: number, scenesBandLength: number, videoLength: number): number {
    const coordInPixels = ((scenesBandLength * coordInSec) / videoLength) * 10
    return coordInPixels
  }

  // Set scenes labels mouse events
  private onLabelMouseUp(): any {
    this.getScenes.sort((a,b) => a.start - b.start)
    const scene: Scene | undefined = this.getScenes.find((s: Scene) => s.id === this.selectedSceneId)
    if (scene && this.didSceneMove) this.updateScene(scene)
    this.allowCreateScenePropagation = true
    document.removeEventListener('mousemove', this.onLabelMouseMove)
    document.removeEventListener('mouseup', this.onLabelMouseUp)
  }

  private onLabelMouseMove(event: MouseEvent): any {
    event.preventDefault()
    const bandLeftX: number = this.sceneBandEl.getBoundingClientRect().left
    const bandRightX: number = this.sceneBandEl.getBoundingClientRect().right
    const sceneNewClientX = event.pageX
    const newOffset: number = sceneNewClientX - bandLeftX
    const newValue: number = Math.round(this.decisecondsFromPixels(newOffset, this.bandWidth, this.videoLengthInDeciseconds))
    const scene: Scene|undefined = this.getScenes.find((s: Scene) => s.id === this.selectedSceneId)
    if (scene && sceneNewClientX > bandLeftX && sceneNewClientX < bandRightX) {
      if (this.isSelectFrame) scene.frame_number = newValue
      else scene.start = newValue
      this.didSceneMove = true
      this.setVideoCurrentTime()
    }
  }

  private onLabelMouseDown(event: MouseEvent, id: number|undefined): void {
    event.preventDefault()
    if (id) this.selectedSceneId = id
    if (!this.lockedScenes.includes(id)) {
      document.addEventListener('mousemove', this.onLabelMouseMove)
      document.addEventListener('mouseup', this.onLabelMouseUp)
    } else {
      this.allowCreateScenePropagation = false
    }
  }

  // Handles mouse hovering the live band

  private handleMouseMoveOverBand(event: MouseEvent): void {
    const video: any = document.getElementById('adVideo')
    video.currentTime = Math.round(this.decisecondsFromPixels(event.offsetX, this.bandWidth, this.videoLengthInDeciseconds)) / 10
  }

  // Set values on component mount
  private setScenesBandResizeEvent(): void {
    window.onresize = ():void => {
      this.bandWidth = this.sceneBandEl.offsetWidth
    }
  }

  private setScenesBandInitialWidth(): void {
    this.bandWidth = this.sceneBandEl.offsetWidth
  }

  private setVideoEventListeners(): void {
    const video: any = document.getElementById('adVideo');
    this.videoEl = video
    if (video) {
      video.onplaying = (event: any): void => {
        this.isVideoPlaying = !this.isVideoPlaying
      };
      video.onpause = (event: any): void => {
        this.isVideoPlaying = !this.isVideoPlaying
      };
      video.ontimeupdate = (event: any): void => {
        this.videoTimestamp = event.target.currentTime
      };
    }
  }

  private setInitialSelectedScene(): void {
    if (this.getScenes && this.getScenes[0]) this.selectedSceneId = Number(this.getScenes[0].id)
  }

  mounted(): any {
    this.sceneBandEl = this.$refs.scenesBand
    this.setScenesBandInitialWidth()
    this.setScenesBandResizeEvent()
    this.setVideoEventListeners()
    if (this.getScenes.length === 0) this.createScene(0)
    this.setInitialSelectedScene()
  }
}

